type Profile = {
  image?: string;
  image_alt?: string;
  image_placeholder?: boolean;
  image_background?: string;
  name?: string;
  role?: string;
  copy?: string;
};

function ProfileImage({
  profile,
  fallbackBackground,
}: {
  profile: Profile;
  fallbackBackground?: string;
}) {
  const backgroundColor =
    profile.image_background || fallbackBackground || "#8AF1D9";

  return (
    <div
      className="aspect-square w-full max-w-sm overflow-hidden"
      style={{ backgroundColor }}
      aria-hidden={profile.image_placeholder ? true : undefined}
    >
      {!profile.image_placeholder && profile.image && (
        <img
          src={profile.image}
          alt={profile.image_alt || profile.name || ""}
          className="h-full w-full object-contain object-bottom"
          loading="lazy"
        />
      )}
    </div>
  );
}

function ProfileText({ profile }: { profile: Profile }) {
  return (
    <div className="max-w-2xl">
      <div className="font-display text-charcoal text-25 leading-tight">
        {profile.name && <p className="font-bold">{profile.name}</p>}
        {profile.role && <p className="font-medium">{profile.role}</p>}
      </div>
      {profile.copy && (
        <p className="font-body text-gray-80 mt-4 text-lg leading-relaxed whitespace-pre-line">
          {profile.copy}
        </p>
      )}
    </div>
  );
}

export function HostEmceeSection({
  heading,
  copy,
  image,
  image_alt,
  image_background = "#8AF1D9",
  name,
  role,
  content_position,
  profiles,
}: {
  heading?: string;
  copy?: string;
  image?: string;
  image_alt?: string;
  image_background?: string;
  name?: string;
  role?: string;
  content_position?: "beside-image" | "below-image";
  profiles?: Profile[];
}) {
  const profileItems =
    profiles && profiles.length > 0
      ? profiles
      : [
          {
            image,
            image_alt,
            image_background,
            name,
            role,
            copy,
          },
        ];

  const contentBelowImage =
    content_position === "below-image" || profileItems.length > 1;

  return (
    <section className="px-4 md:px-8">
      <div className="mx-auto max-w-screen-xl">
        <div className="border-t border-gray-60 py-12 md:py-16">
          {heading && (
            <h2 className="font-display text-charcoal text-[28.4px] font-bold leading-tight max-w-2xl">
              {heading}
            </h2>
          )}
          {profiles && copy && (
            <p className="font-display text-gray-80 mt-4 text-lg font-normal leading-relaxed md:whitespace-nowrap">
              {copy}
            </p>
          )}

          {contentBelowImage ? (
            <div className="mt-8 grid gap-8 md:grid-cols-3">
              {profileItems.map((profile, index) => (
                <div className="max-w-sm" key={`${profile.name || "profile"}-${index}`}>
                  <ProfileImage
                    profile={profile}
                    fallbackBackground={image_background}
                  />
                  <div className="mt-5">
                    <ProfileText profile={profile} />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="mt-8 grid gap-8 md:grid-cols-[minmax(0,22rem)_minmax(0,1fr)] md:items-center">
              <ProfileImage
                profile={profileItems[0]}
                fallbackBackground={image_background}
              />
              <ProfileText profile={profileItems[0]} />
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
